import React, { useCallback, useRef, useState } from "react";
import { Button, Spacer } from "@shoptet/ui";
import { useTranslation } from "react-i18next";
import { AdSystemT } from "../../graphql/generated/graphql";
import { clearBudgetModalReturn, storeBudgetModalReturn } from "../../utils/budgetModalReturn";
import { CreditModal } from "../Credits/Modal";
import { ModalWrapper } from "../ModalWrapper";
import { MonthlyBudget } from ".";

export const EditBudgetModal = ({
  adSystem = AdSystemT.GoogleAdsT,
  initialBudget,
  isOpen,
  onClose,
}: {
  adSystem?: AdSystemT;
  initialBudget?: number;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.MonthlyBudget" });
  const titleKey = adSystem === AdSystemT.GoogleAdsT ? "Edit monthly budget Google" : "Edit monthly budget Meta";

  const [budgetValid, setBudgetValid] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [pendingBudget, setPendingBudget] = useState(initialBudget);
  const [missingCreditAmount, setMissingCreditAmount] = useState<number | undefined>(undefined);
  const saveRef = useRef<(() => Promise<void>) | null>(null);

  const handleBudgetStateChange = useCallback((_budget: number, isValid: boolean) => {
    setBudgetValid(isValid);
  }, []);

  const handleTopUpCredit = (enteredBudget: number, missingCredit: number) => {
    setPendingBudget(enteredBudget);
    setMissingCreditAmount(missingCredit);
    storeBudgetModalReturn(adSystem, enteredBudget);
    setCreditModalOpen(true);
  };

  const handleCreditModalClose = () => {
    clearBudgetModalReturn();
    setCreditModalOpen(false);
  };

  const handleClose = () => {
    setPendingBudget(undefined);
    setMissingCreditAmount(undefined);
    onClose();
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await saveRef.current?.();
      handleClose();
    } catch {
      // Error is displayed by MonthlyBudget component
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ModalWrapper isOpen={isOpen && !creditModalOpen} title={t(titleKey)} maxWidth onClose={handleClose}>
        <MonthlyBudget
          adSystem={adSystem}
          initialMonthlyBudget={pendingBudget ?? initialBudget}
          saveRef={saveRef}
          hideButtons
          hideTooltip
          showCurrentBudget
          onBudgetStateChange={handleBudgetStateChange}
          onTopUpCredit={handleTopUpCredit}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "32px" }}>
          <Button variant="secondary" onClick={handleClose}>
            {t("Close")}
          </Button>
          <Spacer size="sm" />
          <Button
            data-test-id="monthly-budget-save-button"
            disabled={!budgetValid || saving}
            isLoading={saving}
            variant="primary"
            onClick={handleSave}
          >
            {t("Save the change")}
          </Button>
        </div>
      </ModalWrapper>
      {creditModalOpen && (
        <CreditModal initialCustomAmount={missingCreditAmount} isOpen onClose={handleCreditModalClose} />
      )}
    </>
  );
};
